import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAuth } from '@/hooks/useAuth';
import { safeLocalStorage } from "@/lib/safeStorage";

export const PushNotificationPrompt = () => {
  const { user } = useAuth();
  const { isSupported, isSubscribed, requestPermission } = usePushNotifications();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Show prompt if user is logged in, notifications are supported,
    // and user hasn't subscribed yet
    if (user && isSupported && !isSubscribed) {
      // If the OS has already granted permission, the subscription hook will
      // silently (re)subscribe — never prompt again. If it's denied, prompting
      // is pointless. Only prompt when permission is still "default".
      if (typeof Notification === 'undefined' || Notification.permission !== 'default') {
        return;
      }

      // Wait long enough for the subscription check (runs ~8s after mount) to
      // set isSubscribed=true when the user is already subscribed, so we don't
      // flash the prompt on every visit.
      const timer = setTimeout(() => {
        if (Notification.permission !== 'default') return;

        // Detect installed PWA (Android home-screen / standalone display).
        // When installed, Chrome uses a separate storage partition so any
        // existing PushManager subscription from the browser tab is gone —
        // we need to re-prompt once even if the user previously dismissed.
        const isStandalone =
          (typeof window !== 'undefined' &&
            window.matchMedia?.('(display-mode: standalone)').matches) ||
          (typeof navigator !== 'undefined' && (navigator as any).standalone === true);

        const standaloneSeenKey = 'pushPromptStandaloneSeen';
        const seenInStandalone = safeLocalStorage.getItem(standaloneSeenKey);

        if (isStandalone && !seenInStandalone) {
          // First time we see this user inside the installed PWA — re-prompt
          // regardless of any previous browser-tab dismissal.
          safeLocalStorage.setItem(standaloneSeenKey, '1');
          setShowPrompt(true);
          return;
        }

        // Only check dismissal time - show again after 7 days if still not subscribed
        const dismissedAt = safeLocalStorage.getItem('pushPromptDismissedAt');
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

        if (!dismissedAt || parseInt(dismissedAt) < sevenDaysAgo) {
          setShowPrompt(true);
        }
      }, 12000);

      return () => clearTimeout(timer);
    }
  }, [user, isSupported, isSubscribed]);


  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const success = await requestPermission();
      if (success) {
        setShowPrompt(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    safeLocalStorage.setItem('pushPromptDismissedAt', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed right-4 left-4 md:left-auto md:bottom-4 z-50 max-w-sm animate-in slide-in-from-bottom-5 bottom-[calc(5.75rem+env(safe-area-inset-bottom,0px))]">
      <Card className="p-4 shadow-lg">
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold mb-1">Stay Updated!</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Get notified when your friends join activities
            </p>
            
            <div className="flex gap-2">
              <Button onClick={handleEnable} size="sm" className="flex-1" disabled={isLoading}>
                {isLoading ? 'Enabling...' : 'Enable Notifications'}
              </Button>
              <Button onClick={handleDismiss} variant="ghost" size="sm" disabled={isLoading}>
                Later
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
